import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import CssBaseline from '@mui/material/CssBaseline';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import { OnboardingProvider } from './contexts/OnboardingContext';
import { NotificationCenterProvider } from './contexts/NotificationCenterContext';
import { ThemeCustomProvider } from './contexts/ThemeCustomContext';
import ThemeApplier from './components/ThemeApplier';
import { authService } from './services/authService';
import { Box, CircularProgress } from '@mui/material';
import { useState, useEffect } from 'react';
import DashboardPage from './pages/DashboardPage';
import AppointmentsPage from './pages/AppointmentsPage';
import ProfessionalsPage from './pages/ProfessionalsPage';
import PlacesPage from './pages/PlacesPage';
import SchedulesPage from './pages/SchedulesPage';
import AbsencesPage from './pages/AbsencesPage';
import PersonsPage from './pages/PersonsPage';
import PersonDetailPage from './pages/PersonDetailPage';
import TermsOfUsePage from './pages/TermsOfUsePage';
import PagesPage from './pages/PagesPage';
import PageEditorPage from './pages/PageEditorPage';
import ServicesPage from './pages/ServicesPage';
import SupportTicketsPage from './pages/supportTickets/SupportTicketsPage';
import ReportsPage from './pages/ReportsPage';
import AccessPage from './pages/AccessPage';
import FinancePage from './pages/FinancePage';
import PlansPage from './pages/PlansPage';
import BillingReturnPage from './pages/BillingReturnPage';
import UpgradeProvider from './components/plans/UpgradeProvider';
import ApiHealthPage from './pages/ApiHealthCheck/ApiHealthPage';
import IntegrationsPage from './pages/IntegrationsPage';
import CustomFieldsPage from './pages/CustomFieldsPage';
import BillingPage from './pages/BillingPage';
import DomainsPage from './pages/DomainsPage';
import { isDevEnvironment, isPlansSaleEnabled, isIntegrationsEnabled } from './utils/environment';
import { useDomainAccess } from './hooks/useDomainAccess';

import LgpdConsentModal from './components/LgpdConsentModal';
import QueryInvalidator from './components/QueryInvalidator';
// Configuração do React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutos - dados considerados "frescos"
      cacheTime: 10 * 60 * 1000, // 10 minutos - tempo em cache
      refetchOnWindowFocus: false, // Não refetch ao focar janela
      retry: 1, // Tentar apenas 1 vez em caso de erro
    },
  },
});

/**
 * Guarda da rota /domains.
 *
 * ⚠️ Espera a resposta antes de decidir. A versão com flag de build decidia no primeiro render,
 * o que aqui redirecionaria para o dashboard todo link direto para /domains — inclusive o do
 * e-mail de oferta — antes de o servidor ter dito se a conta tem acesso.
 */
function RotaDominios() {
  const { liberado, carregando } = useDomainAccess();

  if (carregando) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return liberado ? <DomainsPage /> : <Navigate to="/dashboard" replace />;
}

/** /integrations → /whatsapp, levando junto ?query e #hash do link antigo. */
function RedirecionaParaWhatsApp() {
  const { search, hash } = useLocation();
  return <Navigate to={`/whatsapp${search}${hash}`} replace />;
}

function App() {
  const [loading, setLoading] = useState(true);

  // Onboarding NÃO mora mais aqui (2026-09-23, docs/PLANO-REMOCAO-AB-WELCOME.md).
  //
  // Este efeito abria o assistente embutido quando o JWT trazia `isNewUser`. Esse assistente foi
  // aposentado: quem cuida do onboarding é o serviço Welcome, para onde o Login manda conta nova
  // direto (`DestinoDoLogin`). O que sobrou aqui é a única coisa que nunca teve a ver com o
  // wizard — consumir o `?token=` que o Login devolve na URL.
  useEffect(() => {
    try {
      authService.processLoginCallback();
      setLoading(false);
    } catch (error) {
      console.warn('[APP] Erro ao processar o retorno do Login:', error);
      setLoading(false);
    }
  }, []);

  if (loading) {
    return (
      <Box
        sx={{
          width: '100%',
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        }}
      >
        <CircularProgress sx={{ color: '#ffffff' }} />
      </Box>
    );
  }

  // Admin normal
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeCustomProvider>
        <ThemeApplier>
          <CssBaseline />
          <OnboardingProvider>
            <NotificationCenterProvider>
              <BrowserRouter>
              <UpgradeProvider>
              <Routes>
                <Route
                  path="/"
                  element={
                    <ProtectedRoute>
                      <Layout />
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<IndexRedirect />} />
                  <Route path="dashboard" element={<DashboardPage />} />
                  <Route path="appointments" element={<AppointmentsPage />} />
                  <Route path="finance" element={<FinancePage />} />
                  {/* Venda de planos oculta por padrão (VITE_FEATURE_PLANS_SALE). Redireciona p/ WhatsApp
                      se disponível, senão p/ dashboard (evita cair em rota também oculta). */}
                  <Route path="plans" element={isPlansSaleEnabled() ? <PlansPage /> : <Navigate to={isIntegrationsEnabled() ? '/whatsapp' : '/dashboard'} replace />} />
                  {/* Plano 19: página de compra (recorte WhatsApp). Mesmo gate de /whatsapp —
                      sem ele, desligar o kill-switch deixaria uma página órfã vendendo recurso escondido. */}
                  <Route path="billing" element={isIntegrationsEnabled() ? <BillingPage /> : <Navigate to="/dashboard" replace />} />
                  <Route path="billing/return" element={<BillingReturnPage />} />
                  <Route path="professionals" element={<ProfessionalsPage />} />
                  <Route path="places" element={<PlacesPage />} />
                  <Route path="services" element={<ServicesPage />} />
                  <Route path="pages" element={<PagesPage />} />
                  <Route path="pages/new/:pageType" element={<PageEditorPage />} />
                  <Route path="pages/edit/:pageId" element={<PageEditorPage />} />
                  <Route path="schedules" element={<SchedulesPage />} />
                  <Route path="persons" element={<PersonsPage />} />
                  <Route path="persons/:id" element={<PersonDetailPage />} />
                  <Route path="custom-fields" element={<CustomFieldsPage />} />
                  <Route path="absences" element={<AbsencesPage />} />
                  <Route path="reports" element={<ReportsPage />} />
                  <Route path="access" element={<AccessPage />} />
                  {/* WhatsApp liberado para todos; kill-switch de emergência = VITE_FEATURE_INTEGRATIONS='false'. */}
                  <Route path="whatsapp" element={isIntegrationsEnabled() ? <IntegrationsPage /> : <Navigate to="/dashboard" replace />} />
                  {/* Endereço antigo (até 04/11/2026): segue valendo em e-mail, push e aviso já enviados. */}
                  <Route path="integrations" element={<RedirecionaParaWhatsApp />} />
                  {/* Venda de domínio: quem libera é o SERVIDOR (204 = não é para este usuário),
                      não uma flag de build. Ver RotaDominios logo abaixo. */}
                  <Route path="domains" element={<RotaDominios />} />
                  <Route path="support-tickets" element={<SupportTicketsPage />} />
                  {isDevEnvironment() && (
                    <Route path="api-health" element={<ApiHealthPage />} />
                  )}
                </Route>
                <Route path="/termos-de-uso" element={<TermsOfUsePage />} />
              </Routes>
              <LgpdConsentModal />
              </UpgradeProvider>
            </BrowserRouter>
            </NotificationCenterProvider>
          </OnboardingProvider>
        </ThemeApplier>
      </ThemeCustomProvider>
      <QueryInvalidator />
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
}

// Redireciona / para /dashboard preservando hash e search.
// Necessário para deep links do tipo /#notification={id} chegarem ao Layout
// sem que o Navigate descarte o hash durante o render.
function IndexRedirect() {
  const location = useLocation();
  return <Navigate to={{ pathname: '/dashboard', search: location.search, hash: location.hash }} replace />;
}
export default App;
